import React from 'react';
import { ReactSortable } from 'react-sortablejs';
import type { ItemInterface, ReactSortableProps } from 'react-sortablejs';
import { Typography } from 'antd';
import { ReorderCard } from '../reorder-card';
import style from './style.module.scss';

type SortableSectionProps<T extends ItemInterface> = {
    title: string;
    viewAllLabel?: string;
    onViewAll?: () => void;
    items: T[];
    setItems: (items: T[]) => void;
    renderItem: (item: T, index: number) => React.ReactNode;
    onAdd?: () => void;
    emptyState?: React.ReactNode;
    disabled?: boolean;
};

export function SortableSection<T extends ItemInterface>({
    title,
    viewAllLabel,
    onViewAll,
    items,
    setItems,
    renderItem,
    onAdd,
    emptyState,
    disabled = false,
}: SortableSectionProps<T>) {
    const effectiveOnAdd = disabled ? undefined : onAdd;
    const showEmptyState = items.length === 0 && emptyState && !effectiveOnAdd;
    const handleListChange: ReactSortableProps<T>['setList'] = (newState) => {
        if (disabled) return;
        setItems(newState);
    };

    return (
        <div>
            <div className={style.section_header}>
                <Typography.Title level={4} className={style.section_title}>
                    {title}
                </Typography.Title>
                {viewAllLabel && onViewAll ? (
                    <button
                        type="button"
                        className={style.view_all}
                        onClick={onViewAll}
                    >
                        {viewAllLabel}
                    </button>
                ) : null}
            </div>
            {showEmptyState ? (
                <div className={style.empty_state}>{emptyState}</div>
            ) : (
                <>
                    <ReactSortable
                        list={items}
                        setList={handleListChange}
                        handle=".reorder_drag_handle"
                        animation={150}
                        className={style.sortable_column}
                        disabled={disabled}
                    >
                        {items.map((item, index) => renderItem(item, index))}
                    </ReactSortable>
                    {effectiveOnAdd && (
                        <div className={style.card_item_placeholder}>
                            <ReorderCard
                                placeholder={true}
                                index={items.length}
                                onAdd={effectiveOnAdd}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
